import { AssistantCaller } from '@paragon/contract-library';

import type { AppDispatch, RootState } from '../../store';
import { describeChangesSinceLastSeen, rememberWhatWasSeen } from '../designerChanges';
import { mergeUndoStepsSince, revertTrussesTo, takeEditCheckpoint } from '../editHistory';
import { AnyAssistantTool, ToolError, ToolResult } from './types';

export type ToolOutcome =
  { readonly ok: true; readonly result: ToolResult } | { readonly ok: false; readonly error: string };

export interface ToolRunOptions {
  readonly caller: AssistantCaller;
  readonly dispatch: AppDispatch;
  readonly getState: () => RootState;

  /**
   * Whether the designer approved a Plan in this Turn. Agents have no Plan step, so this only applies to the Assistant.
   */
  readonly planApproved: boolean;

  readonly chatId?: string;
}

/**
 * Runs a tool the way both the Assistant and Agents must: changes wait for an approved Plan (the Assistant only).
 * Each call undoes as one step, a call that fails puts back the trusses it
 * changed, and each result says what the designer changed since the caller's last call.
 */
export async function runTool(tool: AnyAssistantTool, input: unknown, options: ToolRunOptions): Promise<ToolOutcome> {
  const changes = describeChangesSinceLastSeen(options.caller, options.getState());
  const outcome = withChanges(await runToolChecked(tool, input ?? {}, options), changes);
  rememberWhatWasSeen(options.caller, options.getState());
  return outcome;
}

const CHANGES_NOTE =
  'Made in Paragon since your last call, by the designer or another assistant. Work from the truss as it is now, and ' +
  'keep these changes unless the designer asks otherwise.';

/**
 * Puts what changed since the caller's last call ahead of the result, so it's read before the result is acted on.
 */
function withChanges(outcome: ToolOutcome, changes: object | null): ToolOutcome {
  if (changes == null) return outcome;
  const changedSinceYourLastCall = { note: CHANGES_NOTE, ...changes };
  if (!outcome.ok) return { ok: false, error: `${outcome.error}\n\n${JSON.stringify({ changedSinceYourLastCall })}` };

  const { summary } = outcome.result;
  const isObject = typeof summary === 'object' && summary !== null && !Array.isArray(summary);
  return {
    ok: true,
    result: {
      ...outcome.result,
      summary: { changedSinceYourLastCall, ...(isObject ? summary : { result: summary }) },
    },
  };
}

async function runToolChecked(tool: AnyAssistantTool, input: unknown, options: ToolRunOptions): Promise<ToolOutcome> {
  if (typeof input !== 'object' || input === null || Array.isArray(input))
    return { ok: false, error: 'The input must be an object.' };

  if (options.caller === 'assistant' && tool.changesProject && !options.planApproved)
    return {
      ok: false,
      error: 'This changes the project. Call propose_plan and wait for the designer to approve it first.',
    };

  // Whatever the tool changes undoes as one step, and if it fails, the trusses it changed are put back.
  const checkpoint = takeEditCheckpoint(options.getState());
  try {
    const startedAt = Date.now();
    const result = await tool.execute(input, {
      dispatch: options.dispatch,
      getState: options.getState,
      caller: options.caller,
      ...(options.chatId != null ? { chatId: options.chatId } : {}),
    });
    mergeUndoStepsSince(checkpoint, options.dispatch, options.getState);
    // Many of the app's actions report a failure as an error notification instead of throwing. Pass those on, so the
    // model doesn't take a failed edit for a done one.
    const appErrors = options
      .getState()
      .notifications.snackbars.filter(
        (notification) => notification.addedAt >= startedAt && notification.options.variant === 'error',
      )
      .map((notification) => notification.message);
    if (appErrors.length > 0)
      return {
        ok: false,
        error: `The app reported: ${appErrors.join(' ')} Read the project again to see what, if anything, changed.`,
      };
    return { ok: true, result };
  } catch (error) {
    const reverted = revertTrussesTo(checkpoint, options.dispatch, options.getState);
    return {
      ok: false,
      error:
        describeError(error) +
        (reverted.length === 0
          ? ''
          : ` ${reverted.join(', ')} ${reverted.length === 1 ? 'was put back as it was before this call, so nothing on it' : 'were put back as they were before this call, so nothing on them'} ` +
            'changed.'),
    };
  }
}

// What getActiveCamera throws before the design view has drawn. Matched by text so tools needn't load the view.
const CAMERA_MISSING_MESSAGE = 'The camera has not been created yet.';

function describeError(error: unknown) {
  if (error instanceof ToolError) return error.message;
  // The design view creates its camera only once the page is shown, so a page opened in the background has none.
  if (error instanceof Error && error.message === CAMERA_MISSING_MESSAGE)
    return (
      'The design view hasn’t been drawn yet, which happens when the page was opened or reloaded in the ' +
      'background, so this edit couldn’t run. Ask the designer to bring the Paragon tab to the front, then try again.'
    );
  if (error instanceof Error) return `Unexpected error: ${error.message}`;
  return 'Unexpected error.';
}
